import { useEffect, useCallback, useRef } from 'react';
import { useCallStore } from '../stores/call-store';
import { sipClient } from '../services/sip';
import { wsClient, type WSMessage } from '../services/websocket';
import { apiClient, type Call } from '../services/api';
import { hubspotSDK } from '../hubspot/hubspot-sdk';
import type { SIPConfig } from '../services/sip';

// Transform WebSocket call data to frontend Call type
// The backend sends field names like initiated_at, answered_at, ended_at
// while the frontend uses start_time, answer_time, end_time
function transformWSCallData(data: Record<string, any>): Call {
  return {
    id: data.call_id || data.id,
    call_uuid: data.call_uuid,
    user_id: data.user_id,
    direction: data.direction,
    from_number: data.from_number,
    to_number: data.to_number,
    status: data.status,
    start_time: data.initiated_at,
    answer_time: data.answered_at,
    end_time: data.ended_at,
    duration: data.duration,
    recording_url: data.recording_url,
    hangup_cause: data.hangup_cause,
    caller_id_name: data.caller_id_name,
    metadata: data.metadata,
  };
}

export function useCallManager() {
  const isInitialized = useRef(false);
  const cleanupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const {
    user,
    isConnected,
    isRegistered,
    setConnected,
    setRegistered,
    addCall,
    updateCall,
    removeCall,
    addSIPSession,
    updateSIPSession,
    removeSIPSession,
  } = useCallStore();

  useEffect(() => {
    if (!user) return;

    // Cancel any pending cleanup from StrictMode unmount
    if (cleanupTimeoutRef.current) {
      console.log('useCallManager: Cancelling pending cleanup (StrictMode remount)');
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }

    // Prevent double initialization in React StrictMode
    if (isInitialized.current) {
      console.log('useCallManager: Already initialized, skipping...');
      return;
    }
    isInitialized.current = true;

    const unsubscribers: Array<() => void> = [];

    const initializeSIP = async () => {
      if (!user.sip_username) {
        console.error('User does not have SIP credentials');
        return;
      }

      const sipConfig: SIPConfig = {
        server: import.meta.env.VITE_SIP_SERVER || 'wss://sip.example.com',
        username: user.sip_username,
        password: import.meta.env.VITE_SIP_PASSWORD || '',
        displayName: user.name || user.sip_username,
      };

      try {
        await sipClient.connect(sipConfig);
      } catch (error) {
        console.error('Failed to connect SIP client:', error);
      }
    };

    const initializeWebSocket = () => {
      wsClient.connect(user.id);
      setConnected(true);
    };

    const initializeHubSpot = async () => {
      try {
        await hubspotSDK.initialize();
      } catch (error) {
        console.error('Failed to initialize HubSpot SDK:', error);
      }
    };

    unsubscribers.push(
      sipClient.on('registered', () => {
        console.log('SIP registered');
        setRegistered(true);
      })
    );

    unsubscribers.push(
      sipClient.on('unregistered', () => {
        console.log('SIP unregistered');
        setRegistered(false);
      })
    );

    unsubscribers.push(
      sipClient.on('registrationFailed', (error) => {
        console.error('SIP registration failed:', error);
        setRegistered(false);
      })
    );

    unsubscribers.push(
      sipClient.on('incomingCall', (data) => {
        console.log('Incoming call:', data);
        const session = sipClient.getSession(data.sessionId);
        if (session) {
          addSIPSession(session);
          hubspotSDK.notifyCallStarted({
            phoneNumber: data.from,
            callId: data.sessionId,
            direction: 'inbound',
          });
        }
      })
    );

    unsubscribers.push(
      sipClient.on('callEstablished', (data) => {
        console.log('Call established:', data);
        updateSIPSession(data.sessionId, { state: 'Established' as any });
        // HubSpotWidget handles notifyCallAnswered to avoid duplicate SDK calls
      })
    );

    unsubscribers.push(
      sipClient.on('callEnded', (data) => {
        console.log('Call ended:', data);
        removeSIPSession(data.sessionId);
        // HubSpotWidget handles notifyCallEnded to avoid duplicate SDK calls
      })
    );

    unsubscribers.push(
      wsClient.addMessageHandler((message: WSMessage) => {
        console.log('WebSocket message:', message);

        switch (message.type) {
          case 'call_created':
            addCall(transformWSCallData(message.data));
            break;
          case 'call_updated':
            if (message.data.call_uuid) {
              updateCall(message.data.call_uuid, transformWSCallData(message.data));
            }
            break;
          case 'call_ended':
            console.log('Call ended via WebSocket:', message.data);
            if (message.data.call_uuid) {
              // Pass the full call data from backend to update history with final state
              removeCall(message.data.call_uuid, transformWSCallData(message.data));

              // Also terminate all SIP sessions to ensure the call is fully ended
              // This handles the case where the remote party hung up
              const { sipSessions } = useCallStore.getState();
              sipSessions.forEach((_session, sessionId) => {
                console.log('Terminating SIP session due to call_ended:', sessionId);
                try {
                  sipClient.hangup(sessionId);
                } catch (error) {
                  console.log('SIP session may already be terminated:', error);
                }
                removeSIPSession(sessionId);
              });

              // Tell HubSpot which engagement belongs to this call so it can
              // dismiss its internal IN_PROGRESS tracking entry (prevents ghost duplicate)
              const engagementId = message.data.hubspot_engagement_id;
              if (engagementId) {
                hubspotSDK.notifyCallCompleted({ engagementId: parseInt(engagementId, 10) });
              }
            }
            break;
        }
      })
    );

    hubspotSDK.onOutgoingCall((data) => {
      console.log('HubSpot outgoing call request:', data);
      makeCall(data.phoneNumber);
    });

    initializeSIP();
    initializeWebSocket();
    initializeHubSpot();

    return () => {
      // Delay cleanup to handle React StrictMode double-render
      // If component remounts within 100ms, cleanup will be cancelled
      console.log('useCallManager: Scheduling cleanup...');
      cleanupTimeoutRef.current = setTimeout(() => {
        console.log('useCallManager: Executing cleanup...');
        unsubscribers.forEach((unsub) => unsub());
        sipClient.disconnect();
        wsClient.disconnect();
        setConnected(false);
        setRegistered(false);
        isInitialized.current = false;
        cleanupTimeoutRef.current = null;
      }, 100);
    };
  }, [user]);

  const makeCall = useCallback(
    async (phoneNumber: string) => {
      if (!user) {
        throw new Error('User not authenticated');
      }

      try {
        const sessionId = await sipClient.makeCall(phoneNumber);
        const session = sipClient.getSession(sessionId);
        if (session) {
          addSIPSession(session);
        }

        const call = await apiClient.initiateOutboundCall({
          from: user.phone_number || '',
          to: phoneNumber,
          user_id: user.id,
          record: true,
        });

        addCall(call);

        await hubspotSDK.notifyCallStarted({
          phoneNumber,
          callId: call.call_uuid,
          direction: 'outbound',
        });

        return call.call_uuid;
      } catch (error) {
        console.error('Failed to make call:', error);
        throw error;
      }
    },
    [user, addCall, addSIPSession]
  );

  const answerCall = useCallback(
    async (sessionId: string) => {
      try {
        await sipClient.answer(sessionId);
      } catch (error) {
        console.error('Failed to answer call:', error);
        throw error;
      }
    },
    []
  );

  const hangupCall = useCallback(
    async (sessionId: string, callUUID?: string) => {
      try {
        await sipClient.hangup(sessionId);

        if (callUUID) {
          await apiClient.hangupCall(callUUID);
        }
      } catch (error) {
        console.error('Failed to hangup call:', error);
        throw error;
      }
    },
    []
  );

  const sendDTMF = useCallback(async (sessionId: string, digit: string) => {
    try {
      await sipClient.sendDTMF(sessionId, digit);
    } catch (error) {
      console.error('Failed to send DTMF:', error);
      throw error;
    }
  }, []);

  const holdCall = useCallback(async (sessionId: string) => {
    try {
      await sipClient.hold(sessionId);
    } catch (error) {
      console.error('Failed to hold call:', error);
      throw error;
    }
  }, []);

  const unholdCall = useCallback(async (sessionId: string) => {
    try {
      await sipClient.unhold(sessionId);
    } catch (error) {
      console.error('Failed to unhold call:', error);
      throw error;
    }
  }, []);

  return {
    isConnected,
    isRegistered,
    makeCall,
    answerCall,
    hangupCall,
    sendDTMF,
    holdCall,
    unholdCall,
  };
}
